/**
 * Period and money helpers for the Super Admin Books page (#669).
 *
 * A period key is what the books endpoints take: `YYYY-MM` (a UTC month),
 * `YYYY-MM-1` (the 1st to the 15th) or `YYYY-MM-2` (the 15th to the next 1st).
 * Every boundary is 00:00 UTC; nothing here reads the browser's time zone.
 */

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const SHORT_MONTHS = MONTH_NAMES.map((m) => m.slice(0, 3));

const pad2 = (n) => String(n).padStart(2, "0");

function parse(key) {
    const m = /^(\d{4})-(\d{2})(?:-([12]))?$/.exec(String(key || ""));
    if (!m) throw new Error("invalid books period");
    return { year: Number(m[1]), month: Number(m[2]), half: m[3] ? Number(m[3]) : 0 };
}

function build({ year, month, half }) {
    return `${year}-${pad2(month)}${half ? `-${half}` : ""}`;
}

/** The UTC calendar month of `now`, as `YYYY-MM`. */
export function currentUtcMonth(now = new Date()) {
    return `${now.getUTCFullYear()}-${pad2(now.getUTCMonth() + 1)}`;
}

/** `month`, `first` (1st to 15th) or `second` (15th to the next 1st). */
export function periodKind(key) {
    const { half } = parse(key);
    return half === 0 ? "month" : half === 1 ? "first" : "second";
}

/** Step a period by `delta` units of its own kind (a month or a cycle). */
export function shiftPeriod(key, delta) {
    const p = parse(key);
    // Count in half-month slots for cycles, whole months otherwise.
    const slotsPerMonth = p.half ? 2 : 1;
    const index = (p.year * 12 + (p.month - 1)) * slotsPerMonth + (p.half ? p.half - 1 : 0);
    const next = index + delta;
    const monthIndex = Math.floor(next / slotsPerMonth);
    return build({
        year: Math.floor(monthIndex / 12),
        month: (monthIndex % 12) + 1,
        half: p.half ? (next % slotsPerMonth) + 1 : 0,
    });
}

/** Change the kind of a period, keeping its month. */
export function withKind(key, kind) {
    const p = parse(key);
    return build({ ...p, half: kind === "first" ? 1 : kind === "second" ? 2 : 0 });
}

/** A human label with the UTC dates the period covers. */
export function periodLabel(key) {
    const { year, month, half } = parse(key);
    if (!half) return `${MONTH_NAMES[month - 1]} ${year}`;
    const here = SHORT_MONTHS[month - 1];
    if (half === 1) return `${here} 1 - ${here} 15, ${year} (1st to 15th)`;
    const nextIdx = month % 12;
    return `${here} 15 - ${SHORT_MONTHS[nextIdx]} 1, ${month === 12 ? year + 1 : year} (15th to month end)`;
}

/** Integer cents as dollars; the sign leads the dollar mark. `--` when absent. */
export function formatBooksCents(cents) {
    if (cents === null || cents === undefined) return "--";
    const abs = Math.abs(cents);
    const body = (abs / 100).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${cents < 0 ? "-" : ""}$${body}`;
}

/**
 * Live only on the production app host; everywhere else the figures that exist
 * are Sandbox. Stage shares production's test key and dev carries production
 * credentials, so neither is treated as production here. The selector is always
 * visible, so this is only the starting point.
 */
export function defaultBooksMode(hostname) {
    return hostname === "app.threadflow.com" ? "live" : "sandbox";
}
